/** @file Sync Konva arrow shapes for Drawings + live draft overlay. */
import Konva from "konva";
import { appStore } from "../core/state.js";
import { metersToPx, metersToPxLength } from "../core/coords.js";
import { markDirty } from "./renderer.js";
import { getDraft, onDraftChange } from "../input/tool-fsm.js";

/** @typedef {import("../core/play.js").Drawing} Drawing */
/** @typedef {import("../input/tool-fsm.js").DraftDrawing} DraftDrawing */

const STROKE_M = 0.08;
const MIN_STROKE_PX = 2;
const DASH_ON_M = 0.35;
const DASH_OFF_M = 0.25;

/** @type {Map<string, Konva.Node>} */
const nodes = new Map();
/** @type {Konva.Layer | null} */
let drawingsLayer = null;
/** @type {Konva.Layer | null} */
let overlayLayer = null;
/** @type {Konva.Node | null} */
let draftNode = null;
/** @type {(() => void) | null} */
let unsubStore = null;
/** @type {(() => void) | null} */
let unsubDraft = null;

/**
 * @param {Konva.Layer} draw
 * @param {Konva.Layer} overlay
 */
export function mountDrawings(draw, overlay) {
  drawingsLayer = draw;
  overlayLayer = overlay;

  syncAll();
  unsubStore = appStore.subscribe((next, prev) => {
    if (
      next.drawings !== prev.drawings ||
      next.selectedDrawingId !== prev.selectedDrawingId ||
      next.transform !== prev.transform ||
      next.scenes !== prev.scenes ||
      next.activeSceneIndex !== prev.activeSceneIndex
    ) {
      syncAll();
    }
  });

  unsubDraft = onDraftChange(() => syncDraft());
  syncDraft();
}

export function unmountDrawings() {
  unsubStore?.();
  unsubDraft?.();
  unsubStore = null;
  unsubDraft = null;
  for (const node of nodes.values()) {
    node.destroy();
  }
  nodes.clear();
  draftNode?.destroy();
  draftNode = null;
  drawingsLayer = null;
  overlayLayer = null;
}

function syncAll() {
  const state = appStore.get();
  if (!drawingsLayer || !state.transform) {
    return;
  }

  const activeScene = state.scenes[state.activeSceneIndex];
  const visibleIds = new Set(activeScene?.visibleArrowIds ?? []);

  const seen = new Set();
  for (const drawing of state.drawings) {
    seen.add(drawing.id);
    let node = nodes.get(drawing.id);
    if (node) {
      node.destroy();
    }
    node = buildDrawing(drawing, state.selectedDrawingId === drawing.id);
    node.visible(visibleIds.has(drawing.id));
    nodes.set(drawing.id, node);
    drawingsLayer.add(/** @type {Konva.Shape | Konva.Group} */ (node));
  }

  for (const [id, node] of nodes) {
    if (!seen.has(id)) {
      node.destroy();
      nodes.delete(id);
    }
  }

  markDirty(drawingsLayer);
}

/**
 * Public helper (used by playback engine for test hooks or manual visibility toggles).
 * @param {string} id
 * @param {boolean} visible
 */
export function setArrowVisible(id, visible) {
  const node = nodes.get(id);
  if (node) {
    node.visible(visible);
    if (drawingsLayer) {
      markDirty(drawingsLayer);
    }
  }
}

function syncDraft() {
  if (!overlayLayer) {
    return;
  }
  draftNode?.destroy();
  draftNode = null;
  const draft = getDraft();
  if (draft) {
    const state = appStore.get();
    if (state.transform) {
      draftNode = buildDrawing(
        {
          id: "__draft__",
          type: draft.type,
          color: draft.color,
          points: [draft.start, draft.current],
        },
        false,
        0.6,
      );
      overlayLayer.add(/** @type {Konva.Shape | Konva.Group} */ (draftNode));
    }
  }
  markDirty(overlayLayer);
}

/**
 * @param {Drawing} drawing
 * @param {boolean} selected
 * @param {number} [opacity]
 * @returns {Konva.Node}
 */
function buildDrawing(drawing, selected, opacity = 1) {
  const t = appStore.get().transform;
  if (!t) {
    return new Konva.Group();
  }

  const start = metersToPx(t, drawing.points[0] ?? { x: 0, y: 0 });
  const end = metersToPx(t, drawing.points[drawing.points.length - 1] ?? { x: 0, y: 0 });
  const strokePx = Math.max(MIN_STROKE_PX, metersToPxLength(t, STROKE_M));
  const dashPx = [metersToPxLength(t, DASH_ON_M), metersToPxLength(t, DASH_OFF_M)];

  /** @type {Konva.Node} */
  let node;

  if (drawing.type === "run") {
    node = new Konva.Arrow({
      id: drawing.id,
      points: [start.px, start.py, end.px, end.py],
      stroke: drawing.color,
      fill: drawing.color,
      strokeWidth: strokePx,
      pointerLength: strokePx * 3.5,
      pointerWidth: strokePx * 3,
      lineJoin: "round",
      lineCap: "round",
      opacity,
    });
  } else if (drawing.type === "pass") {
    node = new Konva.Arrow({
      id: drawing.id,
      points: [start.px, start.py, end.px, end.py],
      stroke: drawing.color,
      fill: drawing.color,
      strokeWidth: strokePx,
      dash: dashPx,
      pointerLength: strokePx * 3.5,
      pointerWidth: strokePx * 3,
      lineJoin: "round",
      lineCap: "round",
      opacity,
    });
  } else {
    // shot — zigzag/lightning
    node = buildShotShape(start, end, drawing.color, strokePx, opacity, drawing.id);
  }

  if (selected) {
    if ("shadowColor" in node) {
      /** @type {Konva.Shape} */ (node).shadowColor("#3b82f6");
      /** @type {Konva.Shape} */ (node).shadowBlur(12);
      /** @type {Konva.Shape} */ (node).shadowOpacity(0.9);
    }
  }

  return node;
}

/**
 * @param {{px:number,py:number}} start
 * @param {{px:number,py:number}} end
 * @param {string} color
 * @param {number} strokePx
 * @param {number} opacity
 * @param {string} id
 * @returns {Konva.Node}
 */
function buildShotShape(start, end, color, strokePx, opacity, id) {
  const dx = end.px - start.px;
  const dy = end.py - start.py;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const nx = -uy;
  const ny = ux;
  const segments = Math.max(3, Math.floor(len / 30));
  const amplitude = Math.max(6, strokePx * 3);
  const points = [start.px, start.py];
  for (let i = 1; i < segments; i++) {
    const tRatio = i / segments;
    const bx = start.px + ux * len * tRatio;
    const by = start.py + uy * len * tRatio;
    const side = i % 2 === 0 ? 1 : -1;
    points.push(bx + nx * amplitude * side, by + ny * amplitude * side);
  }
  points.push(end.px, end.py);

  const line = new Konva.Line({
    id,
    points,
    stroke: color,
    strokeWidth: strokePx,
    lineJoin: "round",
    lineCap: "round",
    opacity,
  });

  const head = new Konva.Arrow({
    points: [end.px - ux * 0.001, end.py - uy * 0.001, end.px, end.py],
    stroke: color,
    fill: color,
    strokeWidth: strokePx,
    pointerLength: strokePx * 3.5,
    pointerWidth: strokePx * 3,
    listening: false,
    opacity,
  });

  const group = new Konva.Group({ id });
  group.add(line);
  group.add(head);
  return group;
}

/**
 * @param {import("konva").default.Stage} stage
 * @param {{px:number,py:number}} pxPoint
 * @returns {string | null}
 */
export function drawingIdAt(stage, pxPoint) {
  const shape = stage.getIntersection({ x: pxPoint.px, y: pxPoint.py });
  if (!shape) {
    return null;
  }
  // Walk up: shape or its parent group carries the id
  let current = /** @type {Konva.Node | null} */ (shape);
  while (current) {
    const id = current.id();
    if (id && nodes.has(id)) {
      return id;
    }
    current = current.getParent() ?? null;
  }
  return null;
}
